import React from 'react';
import { FormControl, ListSubheader, MenuItem, Select, SelectChangeEvent } from '@mui/material';
import { DROPDOWN_SX } from '../../constants/formLayout';
import { formatSameAsLabel, PARTICIPANT_MERGE_COPY } from '../../constants/participantMerge';

export interface SameAsTarget {
  participantId: string;
  name: string;
}

interface SameAsSelectProps {
  /** The row's name, for the control's accessible name. */
  rowName: string;
  targets: SameAsTarget[];
  disabled: boolean;
  onSelect: (targetId: string) => void;
}

/** Nothing is ever "selected": picking a target collapses the row instead. */
const NO_SELECTION = '';

/**
 * The menu header. Select clones every child with role="option" and a click
 * handler; this wrapper drops those props so the header is announced and
 * behaves as a heading, not as a choice (MenuList skips it: no tabindex).
 */
const MenuHeader: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <ListSubheader role="presentation">{children}</ListSubheader>
);

/**
 * "Same as…" on a Participants editor row: pick the participant this row is
 * really the same person as. A MUI Select (keyboard reachable) capped with
 * DROPDOWN_SX inside the row's fixed slot — never full width.
 */
const SameAsSelect: React.FC<SameAsSelectProps> = ({ rowName, targets, disabled, onSelect }) => (
  <FormControl size="small" sx={DROPDOWN_SX}>
    <Select<string>
      value={NO_SELECTION}
      displayEmpty
      disabled={disabled}
      renderValue={() => PARTICIPANT_MERGE_COPY.sameAs}
      onChange={(event: SelectChangeEvent<string>) => {
        const targetId = String(event?.target?.value ?? '');
        if (targetId) onSelect(targetId);
      }}
      SelectDisplayProps={{ 'aria-label': `${PARTICIPANT_MERGE_COPY.sameAs} ${rowName}` } as React.HTMLAttributes<HTMLDivElement>}
      sx={{ typography: 'body2' }}
    >
      <MenuHeader>{PARTICIPANT_MERGE_COPY.sameAsHeader}</MenuHeader>
      {targets.map((target) => (
        <MenuItem key={target.participantId} value={target.participantId} aria-label={formatSameAsLabel(target.name)}>
          {target.name}
        </MenuItem>
      ))}
    </Select>
  </FormControl>
);

export default SameAsSelect;
